import { createRoot } from 'react-dom/client';
import moment from 'moment';
import './styles/index.scss';
import Root from './Root';
import { Provider } from 'react-redux';
import { saveState } from './services/localStorage';
import { saveSessionState } from './services/sessionStorage';
import configureStore from './store';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import metadata from './metadata.json';
import { CURRENT_ENV } from './constants';
import { Mixpanel } from './utils/mixpanel';
import { setTranslations, setLocale } from 'react-i18nify';
import { translationsObject } from './i18n';

const env = import.meta.env.VITE_API_ENV || CURRENT_ENV;

Mixpanel.resetSuperProperties();

Sentry.init({
  dsn: 'https://ba475666472a8da826381c3a6f195443@o4507094863839232.ingest.de.sentry.io/4510283136827472"',
  release: 'accumbo-clinic@' + metadata.version,
  environment: env,
  integrations: [new Integrations.BrowserTracing()],
  tracesSampleRate: env === 'production' ? 0.01 : 0.1,
  normalizeDepth: 2,
  ignoreErrors: [
    'Failed to fetch',
    'An error occurred while fetching the data',
    'BodyStreamBuffer was aborted',
    /SSE response code: \d+/,
    'network error',
    'Network Error',
    'Load failed',
    'NetworkError',
    'The network connection was lost',
    'The Internet connection appears to be offline'
  ]
});

const store = configureStore();

const browserLanguage = navigator.language.substring(0, 2);
const storedLanguageSetting = localStorage.getItem('locale');
const defaultLanguage = Object.getOwnPropertyNames(translationsObject).includes(browserLanguage)
  ? browserLanguage
  : 'sv';

setTranslations(translationsObject);
setLocale(storedLanguageSetting || defaultLanguage);

moment.locale(defaultLanguage);

if (!storedLanguageSetting) {
  localStorage.setItem('locale', defaultLanguage);
}

let currentLocale;
let currentAuthToken = {};
let currentProfileImage;
store.subscribe(() => {
  const locale = 'sv'; // todo: hur lagra locale?
  const auth = { ...store.getState().auth };

  if (
    auth.token &&
    (currentAuthToken.jwt !== auth.token.jwt ||
      currentAuthToken.releaseSinceLastLogin !== auth.token.releaseSinceLastLogin ||
      currentProfileImage?.id !== auth.token?.user?.profileImage?.id)
  ) {
    // Delete stuff that shouldn't or can't be serialized
    delete auth.stompClient;
    delete auth.broadcastChannel;

    saveSessionState(auth, 'auth');
    currentAuthToken = auth.token;
    currentProfileImage = auth.token?.user?.profileImage;
  }

  if (currentLocale !== locale) {
    saveState(locale, 'locale');
    moment.locale(locale);
    currentLocale = locale;
  }
});

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <Provider store={store}>
    <Root />
  </Provider>
);
